// src/features/screen/ScreenService.js
import api from "../axios";

// GET endpoints
export const getScreens = async (params = {}) => {
    try {
        const response = await api.get('/screens', { params });
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error fetching screens";
    }
};

export const getScreenById = async (id) => {
    try {
        const response = await api.get(`/screens/screen_id/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error fetching screen";
    }
};

export const searchScreens = async (name) => {
    try {
        const response = await api.get('/screens/search', { params: { name } });
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error searching screens";
    }
};

// POST endpoints (existing)
export const createScreen = async (screenData) => {
    try {
        const formData = new FormData();

        // 1. Add ALL regular fields
        const fields = [
            'name', 'screenType', 'solutionTypeInScreen', 'location',
            'batchScreen', 'description',
            'screenWidth', 'screenHeight',
            'powerSupply', 'powerSupplyQuantity', 'sparePowerSupplyQuantity',
            'receivingCard', 'receivingCardQuantity', 'spareReceivingCardQuantity',
            'mainPowerCable', 'mainPowerCableQuantity', 'spareMainPowerCableQuantity',
            'loopPowerCable', 'loopPowerCableQuantity', 'spareLoopPowerCableQuantity',
            'mainDataCable', 'mainDataCableQuantity', 'spareMainDataCableQuantity',
            'loopDataCable', 'loopDataCableQuantity', 'spareLoopDataCableQuantity',
            'media', 'mediaQuantity', 'spareMediaQuantity',
            'hub', 'hubQuantity', 'spareHubQuantity',
            'fan', 'fanQuantity',
        ];

        fields.forEach(field => {
            const value = screenData[field];
            if (value !== undefined && value !== null) {
                formData.append(field, value); // preserve 0/false
            } else {
                formData.append(field, ''); // fallback empty string
            }
        });

        // 2. Handle irregular pixel pitch fallback
        if (!screenData.irregularPixelPitch) {
            formData.append('pixelPitchWidth', screenData.pixelPitch ?? '');
            formData.append('pixelPitchHeight', screenData.pixelPitch ?? '');
        } else {
            formData.append('pixelPitchWidth', screenData.pixelPitchWidth);
            formData.append('pixelPitchHeight', screenData.pixelPitchHeight);
        }

        // 3. Calculate resolutionWidth and resolutionHeight
        const pixelPitchWidth = screenData.irregularPixelPitch ? screenData.pixelPitchWidth : screenData.pixelPitch;
        const pixelPitchHeight = screenData.irregularPixelPitch ? screenData.pixelPitchHeight : screenData.pixelPitch;
        
        const resolutionWidth = pixelPitchWidth * screenData.screenWidth;
        formData.append("resolutionWidth", resolutionWidth);
        const resolutionHeight = pixelPitchHeight * screenData.screenHeight;
        formData.append("resolutionHeight", resolutionHeight);

        // 3. Solution-specific data
        if (screenData.solutionTypeInScreen === 'MODULE_SOLUTION') {
            if (!screenData.modulesDto) {
                throw new Error("modulesDto is required for MODULE_SOLUTION");
            }
            formData.append(
                'moduleDtoListJson',
                JSON.stringify(screenData.modulesDto.map(m => ({
                    moduleBatchNumber: m.moduleBatchNumber,
                    moduleByWidth: Number(m.moduleByWidth),
                    moduleByHeight: Number(m.moduleByHeight),
                    pixelWidth: Number(m.pixelWidth),
                    pixelHeight: Number(m.pixelHeight)
                })))
            );
        } else if (screenData.solutionTypeInScreen === 'CABINET_SOLUTION') {
            if (!screenData.cabinDtoListJson) {
                throw new Error("cabinDtoListJson is required for CABINET_SOLUTION");
            }
            formData.append('cabinDtoListJson', screenData.cabinDtoListJson);
        }

        // 5. Handle file uploads
        ['connectionFile', 'configFile', 'versionFile'].forEach(fileField => {
            if (screenData[fileField] instanceof File) {
                formData.append(fileField, screenData[fileField]);
            }
        });

        // 6. Debug
        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
            console.log(key, value);
        }

        // 7. Send to API
        const response = await api.post('/screens', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;

    } catch (error) {
        console.error('Error details:', error);
        throw error.response?.data?.message || error.message || "Error creating screen";
    }
};

export const createModules = async (moduleData) => {
    console.log("Create Module");
    console.log(moduleData);
    try {
        const response = await api.post('/module', moduleData);
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error creating module";
    }
};

export const createCabinets = async (cabinetData) => {
    console.log("Create Cabinet");
    console.log(cabinetData);
    try {
        const response = await api.post('/cabin', cabinetData);
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error creating cabin";
    }
};

export const deleteScreen = async (id) => {
    try {
        const response = await api.delete(`/screens/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error deleting screen";
    }
};

export const getScreenWithoutContracts = async () => {
    try {
        const response = await api.get('/screens/without-contracts');
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || "Error fetching screens";
    }
}

export const downloadScreenFile = async (screenId, fileType) => {
    try {
        // API returns a URL (e.g., S3 presigned URL) instead of blob
        const response = await api.get(`/screens/${screenId}/download/${fileType}`);
        
        // Extract the download URL from the response
        const downloadUrl = response.data?.url || response.data || response.data?.downloadUrl;
        
        if (!downloadUrl) {
            throw new Error("No download URL received from server");
        }
        
        // Extract filename from URL or use default
        let filename = `screen_${screenId}_${fileType}`;
        try {
            // Try to extract filename from URL path
            const urlPath = new URL(downloadUrl).pathname;
            const pathParts = urlPath.split('/');
            const lastPart = pathParts[pathParts.length - 1];
            if (lastPart && lastPart.includes('.')) {
                // Remove query parameters if any
                filename = lastPart.split('?')[0];
            }
        } catch (e) {
            // If URL parsing fails, use default filename
            console.warn('Could not extract filename from URL, using default');
        }
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        return true;
    } catch (error) {
        throw new Error(error.response?.data?.message || error.message || "Error downloading file");
    }
}